import type { ReactNode } from "react";

import { LooseEndBrandLockup } from "./BrandLockup";
import { LooseEndLoadingIndicator } from "./LooseEndLoadingIndicator";
import { mayEnterApplication, type AccountGatePhase } from "@/lib/account/gate";

/**
 * WP-04B-R — The required account gate boundary.
 *
 * The application subtree is rendered ONLY in the `ready` phase. Every other
 * phase renders a safe, self-contained screen with no SQL, provider message,
 * token, service-role detail, or raw exception in it.
 */

function GateFrame({ children }: { children: ReactNode }) {
  return (
    <div className="grid min-h-dvh place-items-center bg-background px-6">
      <div
        className="flex max-w-md flex-col items-center text-center"
        data-testid="account-gate-frame"
      >
        <LooseEndBrandLockup variant="full" className="[&_p]:text-center" />
        {children}
      </div>
    </div>
  );
}

export function AccountGateScreen({
  phase,
  onRetry,
}: {
  phase: Exclude<AccountGatePhase, "ready">;
  onRetry?: () => void;
}) {
  if (phase === "loading") {
    return (
      <GateFrame>
        <LooseEndLoadingIndicator
          size={56}
          className="mt-8 animate-fade-in"
          label="Starting the Personal Execution Engine"
        />
        <p className="sr-only" role="status" data-testid="account-gate-loading">
          Preparing your account
        </p>
      </GateFrame>
    );
  }

  if (phase === "blocked") {
    return (
      <GateFrame>
        <h1
          className="mt-8 text-xl font-semibold tracking-tight text-foreground"
          data-testid="account-gate-blocked"
        >
          This account isn't active
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Access to L∞seEnd™ is currently unavailable for this account. If you believe this is a
          mistake, contact support and we'll take a look.
        </p>
      </GateFrame>
    );
  }

  if (phase === "failed") {
    return (
      <GateFrame>
        <h1
          className="mt-8 text-xl font-semibold tracking-tight text-foreground"
          data-testid="account-gate-failed"
        >
          We can't open your workspace
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Your account needs attention before L∞seEnd™ can open. Contact support to continue — no
          Loops were lost.
        </p>
      </GateFrame>
    );
  }

  return (
    <GateFrame>
      <h1
        className="mt-8 text-xl font-semibold tracking-tight text-foreground"
        data-testid="account-gate-unavailable"
      >
        L∞seEnd™ isn't reachable right now
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        We couldn't finish preparing your workspace. This is usually temporary.
      </p>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          data-testid="account-gate-retry"
          className="mt-6 inline-flex min-h-11 items-center justify-center rounded-xl bg-primary px-5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          Try again
        </button>
      ) : null}
    </GateFrame>
  );
}

/** Renders `children` only when the gate permits entry to the app subtree. */
export function AccountGate({
  phase,
  onRetry,
  children,
}: {
  phase: AccountGatePhase;
  onRetry?: () => void;
  children: ReactNode;
}) {
  if (!mayEnterApplication(phase)) {
    return (
      <AccountGateScreen
        phase={phase as Exclude<AccountGatePhase, "ready">}
        {...(onRetry ? { onRetry } : {})}
      />
    );
  }
  return <>{children}</>;
}
